import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #004744;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 4rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background-color: rgba(0, 71, 68, 0.1);
  border: 1px solid #004744;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  color: #004744;
  margin-bottom: 1rem;
  font-style: italic;
`;

const FeatureDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #e0e0e0;
  margin-bottom: 1rem;
`;

const CTASection = styled.div`
  margin-top: 4rem;
  text-align: center;
`;

const Equipment: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  // Equipment features
  const equipmentFeatures = [
    {
      title: language === 'it' ? 'ATTREZZATURE PER LA CASA' : 'HOME EQUIPMENT',
      description: language === 'it' 
        ? 'Dalle cucine completamente attrezzate ai kit di pulizia, forniamo tutto ciò di cui gli ospiti potrebbero aver bisogno durante il loro soggiorno per sentirsi come a casa.'
        : 'From fully equipped kitchens to cleaning kits, we provide everything guests might need during their stay to feel at home.'
    },
    {
      title: language === 'it' ? 'TECNOLOGIA' : 'TECHNOLOGY',
      description: language === 'it' 
        ? 'Smart TV, Wi-Fi ad alta velocità, sistemi audio e altro ancora per garantire che gli ospiti rimangano connessi e intrattenuti.'
        : 'Smart TVs, high-speed Wi-Fi, sound systems, and more to ensure guests stay connected and entertained.'
    },
    {
      title: language === 'it' ? 'COMFORT E BENESSERE' : 'COMFORT & WELLNESS',
      description: language === 'it' 
        ? 'Articoli per il comfort come climatizzatori, riscaldamento, umidificatori e persino attrezzature fitness per rendere il soggiorno degli ospiti confortevole e piacevole.'
        : 'Comfort items like air conditioners, heating, humidifiers, and even fitness equipment to make guests\' stay comfortable and enjoyable.'
    },
    {
      title: language === 'it' ? 'ATTREZZATURE PER BAMBINI' : 'CHILDREN\'S EQUIPMENT',
      description: language === 'it' 
        ? 'Culle, seggioloni, lettini e giocattoli per famiglie che viaggiano con bambini, rendendo il loro soggiorno senza stress.'
        : 'Cribs, high chairs, travel cots, and toys for families traveling with children, making their stay stress-free.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Attrezzatura | J-O-H' : 'Equipment | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Scopri l\'attrezzatura disponibile per rendere il tuo soggiorno confortevole e piacevole' 
            : 'Discover the equipment available to make your stay comfortable and enjoyable'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/property-tenant">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'ATTREZZATURA' : 'EQUIPMENT'}</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'Scopri l\'ampia gamma di attrezzature e forniture disponibili per rendere il tuo soggiorno il più confortevole e piacevole possibile. Da attrezzature per la casa a tecnologia all\'avanguardia, abbiamo tutto ciò di cui potresti aver bisogno.'
              : 'Discover the wide range of equipment and supplies available to make your stay as comfortable and enjoyable as possible. From home equipment to cutting-edge technology, we have everything you might need.'}
          </Description>
          
          <FeaturesGrid>
            {equipmentFeatures.map((feature, index) => (
              <FeatureCard key={index}>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
          
          <Description>
            {language === 'it' 
              ? 'Se hai esigenze specifiche o richieste particolari, non esitare a contattarci. Faremo del nostro meglio per soddisfare le tue richieste e assicurarci che tu abbia tutto ciò di cui hai bisogno durante il tuo soggiorno.'
              : 'If you have specific needs or special requests, do not hesitate to contact us. We will do our best to accommodate your requests and ensure you have everything you need during your stay.'}
          </Description>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci per maggiori informazioni' : 'Contact us for more information'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Equipment; 